export const MIN_YMD = "1970-01-01";
export const MAX_YMD = "2100-12-31";
export const MAX_DAILY_POINTS = 5000;
export const MAX_FUTURE_DAYS = 365;
export const MAX_SPRINT_SPAN_DAYS = 365 * 2;

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

function formatYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function safeParseYmd(ymd: string, minYmd = MIN_YMD, maxYmd = MAX_YMD): Date | null {
  if (typeof ymd !== "string" || !YMD_RE.test(ymd)) return null;
  const [y, m, d] = ymd.split("-").map((v) => Number(v));
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return null;
  if (formatYmd(dt) !== ymd) return null;
  if (ymd < minYmd || ymd > maxYmd) return null;
  return dt;
}

export function isValidYmd(ymd: string, minYmd = MIN_YMD, maxYmd = MAX_YMD): boolean {
  return safeParseYmd(ymd, minYmd, maxYmd) !== null;
}

export function addDaysYmdSafe(ymd: string, days: number): string | null {
  const dt = safeParseYmd(ymd, "0001-01-01", "9999-12-31");
  if (!dt || !Number.isFinite(days)) return null;
  const next = new Date(dt);
  next.setDate(next.getDate() + days);
  return formatYmd(next);
}

export function clampYmdToBounds(ymd: string, minYmd: string, maxYmd: string): string {
  if (!isValidYmd(minYmd, "0001-01-01", "9999-12-31") || !isValidYmd(maxYmd, "0001-01-01", "9999-12-31")) {
    return minYmd;
  }
  if (minYmd > maxYmd) return minYmd;
  const parsed = safeParseYmd(ymd, "0001-01-01", "9999-12-31");
  if (!parsed) return minYmd;
  if (ymd < minYmd) return minYmd;
  if (ymd > maxYmd) return maxYmd;
  return ymd;
}

export function daysBetweenClamped(startYmd: string, endYmd: string, maxDays: number): number | null {
  const s = safeParseYmd(startYmd);
  const e = safeParseYmd(endYmd);
  if (!s || !e || !Number.isFinite(maxDays) || maxDays <= 0) return null;
  const diff = Math.floor((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return null;
  const days = diff + 1;
  if (days > maxDays) return null;
  return days;
}
